'use client';

/**
 * PlanFeatureList — the ``<ul>`` of check-marked feature lines used in
 * every tier card.
 *
 * Feature strings may contain ``<strong>`` tags when the i18n copy
 * emphasizes a count or keyword ("<strong>5 Thinking</strong> + 2 Deep/day").
 * Rendered via a tiny parser that only recognizes ``<strong>…</strong>``
 * pairs — strictly safer than ``dangerouslySetInnerHTML`` because no
 * other HTML can pass through (tags other than ``<strong>`` render as
 * literal text).
 *
 * The check-icon colour varies per tier on the /pricing page (muted for
 * Trial, info for Starter, brand for Plus). Professional plans use the
 * ``brand`` variant uniformly.
 */

import { Check } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export type CheckmarkVariant = 'muted' | 'info' | 'brand' | 'success';

interface PlanFeatureListProps {
  /**
   * Pre-localized feature strings. May contain ``<strong>`` HTML tags
   * for emphasis. Any other tags render as literal text.
   */
  features: string[];
  /**
   * e.g. "Starter plan features" — read aloud by screen readers to
   * associate the ``<ul>`` with its plan card.
   */
  ariaLabel?: string;
  checkVariant?: CheckmarkVariant;
  /**
   * ``stack`` (default) — one feature per line, used inside a vertical
   * tier card. ``inline`` — features wrap left-to-right with a tighter
   * gap, for a single-row summary (e.g. a standalone add-on card that
   * doesn't belong in a tier-comparison grid).
   */
  layout?: 'stack' | 'inline';
  className?: string;
}

const CHECK_COLOR: Record<CheckmarkVariant, string> = {
  muted: 'text-content-muted',
  info: 'text-status-info',
  brand: 'text-brand-tertiary',
  success: 'text-status-success',
};

/**
 * Render a feature string that may contain ``<strong>…</strong>`` pairs.
 *
 * Splitting on the opening/closing tags produces an array where every
 * *odd-indexed* element sat between a ``<strong>`` and ``</strong>``
 * pair — that's the slice we wrap in an actual ``<strong>`` element.
 * Even-indexed elements render as plain text.
 *
 * Why not ``dangerouslySetInnerHTML``: even though i18n strings are
 * trusted today, a future accidental injection via a translation tool
 * or a CMS integration could introduce ``<script>``. The parser here
 * ignores anything that isn't a literal ``<strong>`` pair, so the
 * attack surface is bounded.
 */
function renderFeature(text: string): ReactNode {
  const parts = text.split(/<\/?strong>/);
  // Odd-indexed parts sat between a <strong>…</strong> pair; wrap those in an
  // actual <strong>. Even-indexed parts render as plain text — returning the
  // bare string (rather than a keyed <Fragment>) keeps React happy without the
  // Fragment import, which bun's test transform drops when named-imported
  // alongside an inline `type` from 'react'.
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
  );
}

export function PlanFeatureList({
  features,
  ariaLabel,
  checkVariant = 'brand',
  layout = 'stack',
  className,
}: PlanFeatureListProps) {
  if (features.length === 0) return null;
  const isInline = layout === 'inline';
  return (
    <ul
      className={cn(
        isInline ? 'flex flex-wrap gap-x-5 gap-y-2' : 'space-y-3',
        className
      )}
      aria-label={ariaLabel}
    >
      {features.map((feature, i) => (
        <li
          key={i}
          className={cn(
            'flex gap-2.5',
            isInline ? 'items-center gap-1.5' : 'items-start'
          )}
        >
          <Check
            className={cn(
              'flex-shrink-0',
              isInline ? 'h-3.5 w-3.5' : 'h-4 w-4 mt-0.5',
              CHECK_COLOR[checkVariant]
            )}
            aria-hidden='true'
          />
          <span className='text-sm text-content-secondary leading-snug'>
            {renderFeature(feature)}
          </span>
        </li>
      ))}
    </ul>
  );
}
